/* eslint-disable @typescript-eslint/ban-ts-comment */
import {
  BrowserRouter as Router,
  useLocation,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import MainRoutes from "./routes/MainRoutes";
import AdminRoutes from "./routes/AdminRoutes";
import { ReactNode, useEffect, useState } from "react";
import ForgetPassword from "./pages/ForgetPassword";
import VerifyPage from "./pages/VerifyPage";
import ResetPassword from "./pages/ResetPassword";
import Cookies from "js-cookie";
import Logo from "../src/assets/Logo.svg";
import { useUserDataStore } from "./stores/user";
import Login from "./pages/Login";
import Quiz from "./pages/Quiz";
import Register from "./pages/Register";
import EssayQuiz from "./pages/EssayQuiz";
import Exam from "./pages/Exam";
import ExamPart2 from "./pages/ExamPart2";
import axios from "axios";
import HostedQuiz from "./pages/HostedQuiz";
import { UpdateLatestCountry } from "./api/Students/LatestCountry";
import { useMutation } from "@tanstack/react-query";
import SalesRoutes from "./routes/SalesRoutes";
import IFRSExam from "./pages/IFRSExam";

const fetchCountry = async () => {
  try {
    const response = await axios.get("https://api.country.is/");
    const { country } = response.data;
    localStorage.setItem("userCountry", country);
  } catch (error) {
    console.error("Error fetching the user's country", error);
  }
};

const ProtectedRoute = ({ children }: { children: ReactNode }) => {
  const { userData } = useUserDataStore();
  const userRole = userData?.role;

  if (userRole !== "admin") {
    return <Navigate to="/" replace />;
  } else {
    return children;
  }
};
const SalesProtectedRoute = ({ children }: { children: ReactNode }) => {
  const { userData } = useUserDataStore();
  const userRole = userData?.role;

  if (userRole !== "sales") {
    return <Navigate to="/" replace />;
  } else {
    return children;
  }
};

const StudentPhoneCheck = ({ children }: { children: ReactNode }) => {
  const { userData } = useUserDataStore();
  const location = useLocation();
  const accessToken = Cookies.get("accessToken");

  // Only check phone if user is logged in (has token)
  if (accessToken && userData?.role === "student" && userData?.phone === null) {
    // Don't redirect if already on settings page or auth-related pages
    if (
      !location.pathname.includes("/settings") &&
      !location.pathname.includes("/login") &&
      !location.pathname.includes("/register")
    ) {
      return <Navigate to="/settings?continue-registration=true" replace />;
    }
  }

  return <>{children}</>;
};

const TokenHandler = () => {
  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get("token");

    if (token) {
      Cookies.set("accessToken", token, { expires: 36500 });
    }
  }, [location.search]);

  return null;
};

function App() {
  const { userData, reset } = useUserDataStore();
  const userRole = userData?.role;
  const latestCountry = userData?.lastCountry;
  const currentCountry = localStorage.getItem("userCountry");

  const validateToken = async () => {
    const accessToken = Cookies.get("accessToken");
    if (!accessToken) {
      return;
    }

    try {
      const response = await axios.get(
        "https://nilewisect-backend-production-4eb4.up.railway.app/auth/validate",
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (
        response.status === 401 &&
        response.data.message === "Invalid token"
      ) {
        handleLogout();
      }
    } catch (error) {
      if (
        typeof error === "object" &&
        error !== null &&
        "response" in error &&
        (error as any).response &&
        (error as any).response.status === 401 &&
        (error as any).response.data &&
        (error as any).response.data.message === "Invalid token"
      ) {
        handleLogout();
      } else {
        console.error("Error validating token", error);
      }
    }
  };

  const handleLogout = () => {
    Cookies.remove("accessToken");
    reset();
  };

  const { mutate: mutateCountry } = useMutation({
    mutationFn: () =>
      UpdateLatestCountry({ lastCountry: currentCountry || "EG" }),
    onSuccess: (data) => {
      useUserDataStore.getState().setUserData({ ...data });
    },
    onError: () => {
      console.error("Failed to update country");
    },
  });

  const checkAndUpdateCountry = () => {
    if (latestCountry !== currentCountry && userRole === "student") {
      mutateCountry();
    }
  };

  useEffect(() => {
    // Only set the interval if the user is not an admin
    if (userRole !== "admin") {
      const accessToken = Cookies.get("accessToken");
      if (accessToken) {
        const intervalId = setInterval(() => {
          validateToken();
        }, 60000); // 60000 ms = 1 minute

        return () => clearInterval(intervalId);
      }
    }
  }, [userRole]);

  useEffect(() => {
    const accessToken = Cookies.get("accessToken");
    if (!accessToken) {
      reset();
    } else {
      checkAndUpdateCountry();
    }
  }, [reset, latestCountry, currentCountry]);

  const ScrollToTop = () => {
    const location = useLocation();
    useEffect(() => {
      fetchCountry();
    }, []);

    useEffect(() => {
      window.scrollTo(0, 0);
    }, [location]);

    return null;
  };

  const [isLoading, setIsLoading] = useState(true);

  const handleLoading = () => {
    setIsLoading(false);
  };

  useEffect(() => {
    if (document.readyState === "complete") {
      handleLoading();
    } else {
      window.addEventListener("load", handleLoading);
    }

    return () => {
      window.removeEventListener("load", handleLoading);
    };
  }, []);

  if (isLoading)
    return (
      <div className="min-h-screen flex items-center justify-center flex-col bg-white">
        <img className=" mb-5" src={Logo} alt="Logo" />
        <div className="animate-spin rounded-full sm:size-16 size-10 border-t-[3px] border-b-[3px] border-secondary"></div>
      </div>
    );

  return (
    <Router>
      <ScrollToTop />
      <TokenHandler />
      <StudentPhoneCheck>
        <Routes>
          <Route path="/*" element={<MainRoutes />} />
          <Route
            path="/dashboard/*"
            element={
              <ProtectedRoute>
                <AdminRoutes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales/dashboard/*"
            element={
              <SalesProtectedRoute>
                <SalesRoutes />
              </SalesProtectedRoute>
            }
          />
          <Route path="/forget-password" element={<ForgetPassword />} />
          <Route path="/verify-password/:email" element={<VerifyPage />} />
          <Route path="/reset-password/:email" element={<ResetPassword />} />
          <Route path="/login/admin" element={<Login />} />
          <Route path="/login/sales" element={<Login />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/hosted-quiz/:id" element={<HostedQuiz />} />
          <Route path="/generated-quiz/:id" element={<Quiz />} />
          <Route path="/quiz/:id" element={<Quiz />} />
          <Route path="/ifrs-exam/:id" element={<IFRSExam />} />
          <Route path="/exam/:id" element={<Exam />} />
          <Route path="/exam/:id/part-2" element={<ExamPart2 />} />
          <Route path="/essay-quiz/:id" element={<EssayQuiz />} />
        </Routes>
      </StudentPhoneCheck>
    </Router>
  );
}

export default App;
